export function createQualityBaseSyncService(deps) {
    const {
        fnc,
        entrydataRef,
        entrydetailRef,
        qualityBaseDirtyRef,
        PANEL_TYPE,
        QUALITY_LIST_HABACD_FIELD,
        isEmpty,
        isEnabled,
        DicVal,
        normalizeKakousijiUiString,
        normalizeFukushizaiContext,
        normalizeColorCd,
        isSameFukushizaiItemGroupContext,
        createEmptyFukushizaiContext,
        isQualityBaseField,
        resolveQualityBaseEditContext,
        resolveQualityHsituhabaCdFromLocalSelectionValue,
        qualityTargetColorsForContext,
        ensureDetailRowsByTypeForMutation,
        rowMatchesFukushizaiItemGroupColor,
        createQualityBaseRow,
        setRowContextForPanel,
        mergeQualityRepresentativeRowToEntry
    } = deps;

    // 品質表示BASEの dirty 状態を context 単位で読み出します。
    function readQualityBaseDirtyState(context = null) {
        const dirty = qualityBaseDirtyRef.current || {};
        const dirtyContext = normalizeFukushizaiContext(DicVal(dirty, "context", null));
        const targetContext = context === null ? dirtyContext : normalizeFukushizaiContext(context);
        const fields = DicVal(dirty, "fields", {});

        // dirty 情報が無い場合は空で返します。
        if (isEmpty(fields) || typeof fields !== "object") {
            return { context: targetContext, fields: {} };
        }

        // 別 context の dirty は見せません。
        if (context !== null && !isSameFukushizaiItemGroupContext(dirtyContext, targetContext)) {
            return { context: targetContext, fields: {} };
        }

        return { context: targetContext, fields: { ...fields } };
    }

    // 品質表示BASEの dirty 状態をクリアします。
    function clearQualityBaseDirtyFields(context = null) {
        if (context === null || Object.keys(readQualityBaseDirtyState(context).fields || {}).length !== 0) {
            qualityBaseDirtyRef.current = { context: createEmptyFukushizaiContext(), fields: {} };
        }
    }

    // 品質表示BASEの更新内容を current context の detail へ反映します。
    function applyQualityBaseUpdatesToCurrentContext(updates, contextOverride) {
        const context = normalizeFukushizaiContext(contextOverride);
        const normalizedUpdates = { ...(updates || {}) };
        const updateKeys = Object.keys(normalizedUpdates || {}).filter((key) => isQualityBaseField(key));

        // context または更新対象が無い場合は何もしません。
        if (context.itemCd === "" || context.groupNo === "" || updateKeys.length === 0) {
            return { changed: false, updatedCount: 0, context, skipReason: "missing-context-or-updates" };
        }

        // 品質表示のカラー集合(品質detail行由来)を対象にする。副資材の色構成は参照しない。
        const targetColors = qualityTargetColorsForContext(context);
        if (targetColors.length === 0) {
            return { changed: false, updatedCount: 0, context, skipReason: "missing-target-colors" };
        }

        const hasActualValue = updateKeys.some((key) => normalizeKakousijiUiString(DicVal(normalizedUpdates, key, "")) !== "");
        const detail = ensureDetailRowsByTypeForMutation(PANEL_TYPE.QUALITY_BASE);
        const rows = Array.isArray(detail.rows) ? detail.rows : [];
        let changed = false;
        let updatedCount = 0;

        targetColors.forEach((colorCd) => {
            const normalizedColor = normalizeColorCd(colorCd);
            if (normalizedColor === "") return;

            let index = rows.findIndex((row) => rowMatchesFukushizaiItemGroupColor(row, context.itemCd, context.groupNo, normalizedColor));
            if (index < 0) {
                // 実値が無い場合は新規 row を作らずに終了します。
                if (!hasActualValue) return;
                const created = createQualityBaseRow(context.itemCd, context.groupNo, normalizedColor);
                rows.push(created);
                index = rows.length - 1;
                changed = true;
            }

            const current = rows[index];
            const next = { ...current };
            updateKeys.forEach((key) => {
                const value = DicVal(normalizedUpdates, key, "");
                if (next[key] === value) return;
                next[key] = value;
                changed = true;
            });

            setRowContextForPanel(next, PANEL_TYPE.QUALITY_BASE, context.itemCd, context.groupNo, normalizedColor);
            rows[index] = next;
            updatedCount += 1;
        });

        if (changed) entrydetailRef.current[detail.key] = rows;
        return { changed, updatedCount, context, skipReason: changed ? "" : "no-change" };
    }

    // 品質表示BASE項目1件の変更を detail へ即時反映します。
    function syncQualityBaseFieldValueToDetail(fieldId, value) {
        if (isEmpty(entrydataRef.current) || !isQualityBaseField(fieldId)) return false;

        const contextSnapshot = resolveQualityBaseEditContext();
        if (contextSnapshot.itemCd === "" || contextSnapshot.groupNo === "") return false;

        let syncValue = value;
        if (fieldId === QUALITY_LIST_HABACD_FIELD) {
            const selectedCode = resolveQualityHsituhabaCdFromLocalSelectionValue(value);
            if (selectedCode !== "") syncValue = selectedCode;
        }

        const updates = { [fieldId]: syncValue };
        qualityBaseDirtyRef.current = { context: contextSnapshot, fields: updates };

        const result = applyQualityBaseUpdatesToCurrentContext(updates, contextSnapshot);
        clearQualityBaseDirtyFields(contextSnapshot);

        if (isEnabled(DicVal(result, "changed", false))) {
            mergeQualityRepresentativeRowToEntry(entrydataRef.current, entrydetailRef.current, contextSnapshot);
            if (!isEmpty(fnc.current) && typeof fnc.current.modify === "function") fnc.current.modify(true);
        }

        return isEnabled(DicVal(result, "changed", false));
    }

    return {
        readQualityBaseDirtyState,
        clearQualityBaseDirtyFields,
        applyQualityBaseUpdatesToCurrentContext,
        syncQualityBaseFieldValueToDetail
    };
}
